import {
  parseCogoSurfaceProposalText,
  type AgentSurfaceProposalExpectation,
  type CogoSurfaceProposal,
} from '@cogo/a2a-gateway/agent-surface-contract'
import {
  agentSurfaceReplyRefusal,
  agentSurfaceRequestRefusal,
  declaresCogoSurfaceProposal,
  type AgentSurfaceRefusal,
} from './agent-surface-admission-reading'

export type { CogoSurfaceProposal } from '@cogo/a2a-gateway/agent-surface-contract'
export type {
  AgentSurfaceRefusal,
  AgentSurfaceRefusalOrigin,
  AgentSurfaceRefusalReason,
} from './agent-surface-admission-reading'

const A2A_VERSION_HEADER = 'A2A-Version'
const A2A_PROTOCOL_VERSION = '1.0'
const SEND_MESSAGE_METHOD = 'SendMessage'
/** message.metadata envelope key — interpreters extract { cogo: { skillId?, params? } }. */
export const COGO_A2A_METADATA_KEY = 'cogo'
const USER_ROLE = 1

export interface AgentCardResult {
  ok: boolean
  status: number
  card: unknown
}

export interface UnauthorizedRpcBody {
  jsonrpc: '2.0'
  id: unknown
  error: { code: -32603; message: 'unauthorized' }
}

export type PostA2AAuth =
  | { headerName: string; key: string }
  | { accessToken: string }

export type PostA2AOptions = PostA2AAuth & { body: unknown }

export type PostA2AResult =
  | {
      ok: false
      unauthorized: true
      status: number
      body: UnauthorizedRpcBody
    }
  | {
      ok: boolean
      unauthorized: false
      status: number
      body: unknown
    }

function hasOwn(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function isUnauthorizedRpcBody(body: unknown): body is UnauthorizedRpcBody {
  if (typeof body !== 'object' || body === null) return false
  if (!hasOwn(body, 'jsonrpc') || !hasOwn(body, 'id') || !hasOwn(body, 'error')) {
    return false
  }
  const record = body as { jsonrpc: unknown; id: unknown; error: unknown }
  if (record.jsonrpc !== '2.0') return false
  if (typeof record.error !== 'object' || record.error === null) return false
  if (!hasOwn(record.error, 'code') || !hasOwn(record.error, 'message')) return false
  const error = record.error as { code: unknown; message: unknown }
  return error.code === -32603 && error.message === 'unauthorized'
}

/**
 * HTTP 500 + JSON-RPC unauthorized is the GATEWAY refusing a shared key.
 *
 * Deliberately NOT widened to HTTP 401, which is the EDGE refusing a bearer.
 * The two have different remedies -- fix the key vs sign in again -- and the
 * flag exists to tell them apart. Callers that care about the session case
 * read `status === 401`; see `studio-agent-runtime-a2a.test.ts`, which pins
 * this boundary.
 */
function isUnauthorizedDenial(
  status: number,
  body: unknown,
): body is UnauthorizedRpcBody {
  return status === 500 && isUnauthorizedRpcBody(body)
}

function sharedKeyHeaderName(headerName: string): string {
  const name = headerName.trim()
  const lower = name.toLowerCase()
  if (lower === 'a2a-version' || lower === 'content-type') {
    throw new Error(
      `shared-key header name must not collide with ${JSON.stringify(name)}`,
    )
  }
  return name
}

const APPLICATION_ID = /^[a-z0-9][a-z0-9-]{0,62}$/

/** Same-origin baseUrl for the appliance edge. Does not assemble container DNS. */
export function applianceAgentRuntimeBaseUrl(origin: string, applicationId: string): string {
  const id = applicationId.trim()
  if (!APPLICATION_ID.test(id)) {
    throw new Error("application id must be a lowercase DNS-style slug")
  }
  return `${origin.replace(/\/$/, "")}/apps/${id}`
}

/**
 * A2A v1.0 text Part. `{ kind: "text", text }` is v0.3 and is silently dropped.
 * Matches the gateway adapter `textPart` wire: `$case: "text"`.
 */
export function a2aTextPart(value: string): {
  content: { $case: 'text'; value: string }
  metadata: undefined
  filename: string
  mediaType: string
} {
  return { content: { $case: 'text', value }, metadata: undefined, filename: '', mediaType: '' }
}

export interface CogoMessageEnvelope {
  skillId?: string
  params?: Record<string, unknown>
}

export interface SendMessageParams {
  text: string
  messageId: string
  id?: unknown
  skillId?: string
  params?: Record<string, unknown>
}

export type SendMessageOptions = SendMessageParams & PostA2AAuth

function cogoMetadata(
  envelope: CogoMessageEnvelope,
): { cogo: { skillId?: string; params?: Record<string, unknown> } } | undefined {
  const cogo: { skillId?: string; params?: Record<string, unknown> } = {}
  if (envelope.skillId !== undefined) {
    cogo.skillId = envelope.skillId
  }
  if (envelope.params !== undefined) {
    cogo.params = envelope.params
  }
  if (cogo.skillId === undefined && cogo.params === undefined) {
    return undefined
  }
  return { [COGO_A2A_METADATA_KEY]: cogo }
}

/** JSON-RPC SendMessage body the appliance `/a2a` fetch tests post. */
export function sendMessageRpcBody(options: SendMessageParams): {
  jsonrpc: '2.0'
  id: unknown
  method: 'SendMessage'
  params: {
    message: {
      messageId: string
      role: 1
      parts: ReturnType<typeof a2aTextPart>[]
      metadata?: { cogo: { skillId?: string; params?: Record<string, unknown> } }
    }
  }
} {
  const metadata = cogoMetadata({ skillId: options.skillId, params: options.params })
  return {
    jsonrpc: '2.0',
    id: options.id ?? 1,
    method: SEND_MESSAGE_METHOD,
    params: {
      message: {
        messageId: options.messageId,
        role: USER_ROLE,
        parts: [a2aTextPart(options.text)],
        ...(metadata ? { metadata } : {}),
      },
    },
  }
}

export type AgentRuntimeClientOptions = {
  origin: string
  applicationId: string
} & PostA2AAuth

/** Card-advertised launcher header name. The key is never a card field. */
export function headerNameFromAgentCard(card: unknown): string | undefined {
  if (typeof card !== 'object' || card === null || !hasOwn(card, 'securitySchemes')) {
    return undefined
  }
  const schemes = (card as { securitySchemes: unknown }).securitySchemes
  if (typeof schemes !== 'object' || schemes === null || !hasOwn(schemes, 'gatewaySharedKey')) {
    return undefined
  }
  const scheme = (schemes as { gatewaySharedKey: unknown }).gatewaySharedKey
  if (typeof scheme !== 'object' || scheme === null || !hasOwn(scheme, 'name')) {
    return undefined
  }
  const name = (scheme as { name: unknown }).name
  return typeof name === 'string' && name.trim().length > 0 ? name : undefined
}

export const KG_SPARQL_DEPLOY_PROBE_QUERY = "SELECT ?s WHERE { ?s ?p ?o }"
export const GEMMA_VISUAL_REVIEW_SKILL_ID = "gemma-visual-review"
export const COGO_SURFACE_PREVIEW_SKILL_ID = "cogo-surface-preview"

/** Extracts only the preview-only proposal from the gateway execution artifact. */
/**
 * Yields every JSON value in an artifact that could be a surface proposal:
 * the artifact itself, then each node output of an execution-result envelope.
 * Ordering is deliberate -- a bare proposal artifact still wins if one is ever
 * emitted, and nothing here relaxes admission.
 *
 * Each candidate carries BOTH the text admission parses and the value a
 * refusal is classified from, so the two cannot drift into describing
 * different candidates.
 */
function proposalCandidates(text: string): { text: string; value: unknown }[] {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return [{ text, value: undefined }]
  }
  const candidates = [{ text, value: parsed }]
  if (typeof parsed !== "object" || parsed === null) return candidates
  const outputs = (parsed as { outputs?: unknown }).outputs
  if (typeof outputs === "object" && outputs !== null && !Array.isArray(outputs)) {
    for (const value of Object.values(outputs as Record<string, unknown>)) {
      if (typeof value === "object" && value !== null) {
        candidates.push({ text: JSON.stringify(value), value })
      }
    }
  }
  return candidates
}

/**
 * The four facts an A2A reply can carry about a surface. Not three, and never
 * one.
 */
export type AgentSurfaceReading =
  | { outcome: "admitted"; proposal: CogoSurfaceProposal }
  | { outcome: "refused"; refusal: AgentSurfaceRefusal }
  | { outcome: "absent" }

/**
 * What the reply had to say about a surface.
 *
 * This used to be `CogoSurfaceProposal | null`, and the null said four things
 * at once: admitted-nothing, refused-because-the-identity-is-not-in-the-
 * catalogue, refused-for-a-clause-the-contract-stated, and there-was-no-
 * surface-here. A reader given only the null renders "nothing appeared", and
 * anyone debugging a refused surface debugs the wrong half -- which is exactly
 * what the T3 note predicts for a generated surface
 * (`packages/cogo-a2a-gateway/docs/t3-generation-arc.md` §1).
 *
 * Admission is UNCHANGED. Every candidate still goes through
 * `parseCogoSurfaceProposalText` with the caller's expectation, so a
 * mismatched scenario, surface kind or base revision is still refused, and an
 * identity outside the closed catalogue is still refused. Only the reporting
 * is new.
 *
 * A candidate that never claimed to be a proposal is not a refusal: the
 * execution envelope and every prose/data node output ride in the same
 * artifact, and reporting their shape complaints would bury the real refusal.
 * First refusal wins -- in the live shape the envelope is skipped and the
 * proposal is the only claimant.
 */
export function readCogoSurfaceProposal(
  body: unknown,
  expectation: AgentSurfaceProposalExpectation,
): AgentSurfaceReading {
  const requestRefusal = agentSurfaceRequestRefusal(expectation)
  if (requestRefusal) return { outcome: "refused", refusal: requestRefusal }

  const absent = { outcome: "absent" } as const
  if (typeof body !== "object" || body === null || !hasOwn(body, "result")) return absent
  const result = (body as { result: unknown }).result
  if (typeof result !== "object" || result === null || !hasOwn(result, "task")) return absent
  const task = (result as { task: unknown }).task
  if (
    typeof task !== "object" ||
    task === null ||
    !hasOwn(task, "status") ||
    !hasOwn(task, "artifacts")
  ) return absent
  const status = (task as { status: unknown }).status
  if (
    typeof status !== "object" ||
    status === null ||
    !hasOwn(status, "state") ||
    (status as { state: unknown }).state !== "TASK_STATE_COMPLETED"
  ) return absent
  const artifacts = (task as { artifacts: unknown }).artifacts
  if (!Array.isArray(artifacts)) return absent

  let refusal: AgentSurfaceRefusal | undefined
  for (const artifactValue of artifacts) {
    if (typeof artifactValue !== "object" || artifactValue === null) continue
    const artifact = artifactValue as Record<string, unknown>
    if (artifact.artifactId !== "coogo-execution-result") continue
    if (!Array.isArray(artifact.parts)) continue
    for (const part of artifact.parts) {
      const text = partText(part)
      if (!text) continue
      // `coogo-execution-result` carries the PLAN EXECUTION RESULT, so the
      // proposal is a node output inside it -- see the gateway adapter, which
      // serialises `{ planId, runId, outputs: { <nodeId>: <output> }, ... }`.
      for (const candidate of proposalCandidates(text)) {
        try {
          return {
            outcome: "admitted",
            proposal: parseCogoSurfaceProposalText(candidate.text, expectation),
          }
        } catch (error) {
          if (refusal === undefined && declaresCogoSurfaceProposal(candidate.value)) {
            refusal = agentSurfaceReplyRefusal(candidate.value, error)
          }
        }
      }
    }
  }
  return refusal === undefined ? absent : { outcome: "refused", refusal }
}

/**
 * The admitted proposal, or null.
 *
 * Kept for callers that only route on "is there a surface to draw". A caller
 * that wants to TELL THE USER why there is not must read
 * `readCogoSurfaceProposal` instead -- this signature cannot carry the reason.
 */
export function cogoSurfaceProposalFromRpcBody(
  body: unknown,
  expectation: AgentSurfaceProposalExpectation,
): CogoSurfaceProposal | null {
  const reading = readCogoSurfaceProposal(body, expectation)
  return reading.outcome === "admitted" ? reading.proposal : null
}

export interface SkillParamContract {
  required: string[]
  optional: string[]
}

/**
 * The parameter contract a card declares for one skill, or undefined when the
 * card says nothing.
 *
 * The distinction is load-bearing: an EMPTY contract means "takes nothing" and
 * the caller may send the skill bare, while an ABSENT one means the agent has
 * not published its contract and the caller cannot know what to send. The two
 * must not be collapsed -- an appliance running a gateway from before
 * paramContract existed publishes silence, and treating that as "takes
 * nothing" would resurrect the guessing this field removed.
 */
export function skillParamContractFromAgentCard(
  card: unknown,
  skillId: string,
): SkillParamContract | undefined {
  if (typeof card !== "object" || card === null || !hasOwn(card, "skills")) return undefined
  const skills = (card as { skills: unknown }).skills
  if (!Array.isArray(skills)) return undefined
  for (const skill of skills) {
    if (typeof skill !== "object" || skill === null) continue
    if ((skill as { id?: unknown }).id !== skillId) continue
    if (!hasOwn(skill, "paramContract")) return undefined
    const contract = (skill as { paramContract: unknown }).paramContract
    if (typeof contract !== "object" || contract === null) return undefined
    const required = (contract as { required?: unknown }).required
    const optional = (contract as { optional?: unknown }).optional
    if (!Array.isArray(required) || !Array.isArray(optional)) return undefined
    return {
      required: required.filter((entry): entry is string => typeof entry === "string"),
      optional: optional.filter((entry): entry is string => typeof entry === "string"),
    }
  }
  return undefined
}

/**
 * The required params the caller has nothing to put in.
 *
 * Exists so the two reasons a call cannot be made keep separate names.
 * `paramsForSkill` answers undefined for BOTH "the agent published no
 * contract" and "the contract cannot be filled from here", and a caller that
 * reports one message for both will sometimes report the wrong one --
 * measured live 2026-08-24, Review Bot was refused with "this bot does not
 * publish its input contract" while the card published it in full and the
 * real gap was that the screen has no screenshots. A wrong reason is worse
 * than a vague one: it sends the reader to look at the agent when the gap is
 * on the caller's side.
 *
 * An unknown contract yields an EMPTY list, not every name: nothing is
 * unfillable because nothing is declared. That is the other state, and the
 * caller distinguishes them by asking for the contract first.
 */
export function unfillableRequiredParams(
  contract: SkillParamContract | undefined,
  supply: Record<string, unknown>,
): string[] {
  if (!contract) return []
  return contract.required.filter((name) => {
    const value = supply[name]
    return value === undefined || value === null || value === ""
  })
}

/**
 * Fills a declared contract from what the caller actually has.
 *
 * `supply` is the caller's own material, keyed by param name. A required param
 * with nothing to put in it means the call CANNOT be made correctly, and this
 * returns undefined rather than sending a request the agent will reject --
 * refusing here turns a server round trip into a local, nameable failure.
 */
export function paramsForSkill(
  contract: SkillParamContract | undefined,
  supply: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!contract) return undefined
  const params: Record<string, unknown> = {}
  for (const name of contract.required) {
    const value = supply[name]
    if (value === undefined || value === null || value === "") return undefined
    params[name] = value
  }
  for (const name of contract.optional) {
    const value = supply[name]
    if (value !== undefined && value !== null && value !== "") params[name] = value
  }
  return params
}

/**
 * What a FAILED task says when it names no code at all. Not a refusal — the
 * state is the whole fact — so `rosterRefusalFromRpcBody` filters it out.
 */
export const GENERIC_TASK_FAILURE_CODE = "task_failed"

/** `message.metadata.code`, when the reply declares one. */
function declaredCode(message: object): string | undefined {
  if (!hasOwn(message, "metadata")) return undefined
  const metadata = (message as { metadata: unknown }).metadata
  if (typeof metadata !== "object" || metadata === null || !hasOwn(metadata, "code")) return undefined
  const raw = (metadata as { code: unknown }).code
  return typeof raw === "string" && raw.length > 0 ? raw : undefined
}

/**
 * The code the text part prefixes itself with.
 *
 * Only consulted when `metadata.code` is absent. The producer writes both, so
 * this is the belt to that braces — a proxy or a transport that drops
 * `metadata` must not be able to turn a named refusal back into "task failed".
 */
function codeFromText(detail: string): string | undefined {
  return CODE_PREFIXED_TEXT.exec(detail)?.[1]
}

/** The first non-empty text part of a message, or "". */
function firstPartText(message: object): string {
  if (!hasOwn(message, "parts") || !Array.isArray((message as { parts: unknown }).parts)) return ""
  for (const part of (message as { parts: unknown[] }).parts) {
    const value = partText(part)
    if (typeof value === "string" && value.length > 0) return value
  }
  return ""
}

export interface A2aTaskFailure {
  code: string
  detail: string
}

/**
 * The failure a task reports, in the server's own words.
 *
 * `rosterRefusalFromRpcBody` above answers a CLOSED vocabulary -- the two
 * refusals Studio routes on. Everything else used to fall through as "no
 * surface to render", so a live `invalid_params: agent-answer: prompt:
 * Required` was presented to the user as nothing having gone wrong (measured
 * 2026-08-24). This one is open by construction: any FAILED state is a
 * failure, and a missing `metadata.code` degrades to `task_failed` rather than
 * back to silence.
 */
/**
 * A JSON-RPC error envelope: `{ jsonrpc, id, error: { code, message } }`.
 *
 * It carries no `result`, so every reader that walks `result.task` -- roster
 * refusal, task failure, surface proposal, answer text, row set -- returns
 * nothing on it and the caller's absence fallback catches it. Measured live
 * 2026-08-25: the appliance answered `POST /apps/cogo-bot/a2a` with 500 and
 * `{"error":{"code":-32603,"message":"unauthorized"}}` -- the runtime refusing
 * a request that carried no identity assertion -- and Studio told the user
 * there was nothing to render, which sent the debugging to the wrong half.
 *
 * An envelope with neither a numeric code nor a message is not reported: it
 * says nothing the reader could put on screen, and inventing a code for it
 * would make an empty object look like a server verdict.
 */
export function rpcErrorFromRpcBody(
  body: unknown,
): { code: number; message: string } | undefined {
  if (typeof body !== "object" || body === null || !hasOwn(body, "error")) return undefined
  const error = (body as { error: unknown }).error
  if (typeof error !== "object" || error === null) return undefined
  const rawCode = hasOwn(error, "code") ? (error as { code: unknown }).code : undefined
  const rawMessage = hasOwn(error, "message") ? (error as { message: unknown }).message : undefined
  const code = typeof rawCode === "number" ? rawCode : 0
  const message = typeof rawMessage === "string" ? rawMessage : ""
  if (code === 0 && message.length === 0) return undefined
  return { code, message }
}

export function taskFailureFromRpcBody(body: unknown): A2aTaskFailure | undefined {
  if (typeof body !== "object" || body === null || !hasOwn(body, "result")) return undefined
  const result = (body as { result: unknown }).result
  if (typeof result !== "object" || result === null || !hasOwn(result, "task")) return undefined
  const task = (result as { task: unknown }).task
  if (typeof task !== "object" || task === null || !hasOwn(task, "status")) return undefined
  const status = (task as { status: unknown }).status
  if (typeof status !== "object" || status === null) return undefined
  if ((status as { state?: unknown }).state !== "TASK_STATE_FAILED") return undefined

  const message = hasOwn(status, "message") ? (status as { message: unknown }).message : undefined
  if (typeof message !== "object" || message === null) {
    return { code: GENERIC_TASK_FAILURE_CODE, detail: "" }
  }
  const detail = firstPartText(message)
  const code = declaredCode(message) ?? codeFromText(detail) ?? GENERIC_TASK_FAILURE_CODE
  return { code, detail }
}

/**
 * The prose answer a completed task carries, if it carries one.
 *
 * Measured live 2026-08-24: the appliance answered in 874ms with the reply in
 * `outputs.n1.text`, and the caller showed "no surface to render" -- the whole
 * arc worked and the last step discarded the result.
 *
 * A surface proposal arrives in the SAME envelope, so this returns undefined
 * when the outputs carry no `text`: printing the JSON of a proposal at the
 * user would be worse than saying nothing. Multiple output nodes are joined
 * rather than first-wins, because dropping the rest would silently lose half
 * a reply.
 */
/**
 * Every node output a COMPLETED task carries, in plan order.
 *
 * ONE envelope walker. Both reply readers below are built on it, so they
 * cannot drift into disagreeing about where outputs live -- if they did, one
 * of the two kinds of reply would silently stop arriving and the fallback
 * would claim the agent sent nothing.
 */
function executionOutputs(body: unknown): unknown[] | undefined {
  if (typeof body !== "object" || body === null || !hasOwn(body, "result")) return undefined
  const result = (body as { result: unknown }).result
  if (typeof result !== "object" || result === null || !hasOwn(result, "task")) return undefined
  const task = (result as { task: unknown }).task
  if (typeof task !== "object" || task === null || !hasOwn(task, "status")) return undefined
  const status = (task as { status: unknown }).status
  if (
    typeof status !== "object" ||
    status === null ||
    (status as { state?: unknown }).state !== "TASK_STATE_COMPLETED"
  ) return undefined
  const artifacts = hasOwn(task, "artifacts") ? (task as { artifacts: unknown }).artifacts : undefined
  if (!Array.isArray(artifacts)) return undefined

  const collected: unknown[] = []
  for (const artifactValue of artifacts) {
    if (typeof artifactValue !== "object" || artifactValue === null) continue
    const artifact = artifactValue as Record<string, unknown>
    if (artifact.artifactId !== "coogo-execution-result") continue
    if (!Array.isArray(artifact.parts)) continue
    for (const part of artifact.parts) {
      const text = partText(part)
      if (typeof text !== "string") continue
      let parsed: unknown
      try {
        parsed = JSON.parse(text)
      } catch {
        continue
      }
      if (typeof parsed !== "object" || parsed === null) continue
      const outputs = (parsed as { outputs?: unknown }).outputs
      if (typeof outputs !== "object" || outputs === null || Array.isArray(outputs)) continue
      collected.push(...Object.values(outputs as Record<string, unknown>))
    }
  }
  return collected
}

export function agentAnswerTextFromRpcBody(body: unknown): string | undefined {
  const outputs = executionOutputs(body)
  if (!outputs) return undefined
  const answers: string[] = []
  for (const output of outputs) {
    if (typeof output !== "object" || output === null || Array.isArray(output)) continue
    const value = (output as { text?: unknown }).text
    if (typeof value === "string" && value.trim().length > 0) answers.push(value)
  }
  return answers.length > 0 ? answers.join("\n\n") : undefined
}

export interface AgentDataResult {
  rowCount: number
  rows: unknown[]
}

/**
 * The row set a query-shaped skill returned.
 *
 * Measured live 2026-08-24: kg-sparql answered `outputs.n1: []` -- the query
 * ran and matched nothing, because the appliance's store holds no triples yet
 * -- and the caller reported "no surface to render", which reads as a client
 * fault. Zero rows is a RESULT, and saying so is the whole point of this
 * reader.
 *
 * Only ARRAY outputs qualify. Prose and surface proposals are objects owned by
 * the other readers; claiming them here would double-render the same reply.
 */
export function agentDataResultFromRpcBody(body: unknown): AgentDataResult | undefined {
  const outputs = executionOutputs(body)
  if (!outputs) return undefined
  const rows: unknown[] = []
  let sawArray = false
  for (const output of outputs) {
    if (!Array.isArray(output)) continue
    sawArray = true
    rows.push(...output)
  }
  return sawArray ? { rowCount: rows.length, rows } : undefined
}

/**
 * What the AGENT calls one of its skills.
 *
 * Exists so a UI can label a capability without inventing a name for it. Studio
 * shipped a hand-written table -- "App Bot", "Review Bot", "Answer Bot",
 * "Knowledge Bot" -- for four skills of ONE agent, which told the reader there
 * were four bots where the card declares four `skills[]` entries. The card is
 * the only authority for what a capability is called, the same way it is the
 * authority for whether the capability exists at all.
 *
 * Returns undefined when the card names nothing, so the caller falls back to
 * the skill id rather than to a label this file made up.
 */
export function skillNameFromAgentCard(card: unknown, skillId: string): string | undefined {
  if (typeof card !== "object" || card === null || !hasOwn(card, "skills")) return undefined
  const skills = (card as { skills: unknown }).skills
  if (!Array.isArray(skills)) return undefined
  for (const skill of skills) {
    if (typeof skill !== "object" || skill === null) continue
    if ((skill as { id?: unknown }).id !== skillId) continue
    const name = hasOwn(skill, "name") ? (skill as { name: unknown }).name : undefined
    return typeof name === "string" && name.trim().length > 0 ? name : undefined
  }
  return undefined
}

export function skillIdsFromAgentCard(card: unknown): string[] {
  if (typeof card !== "object" || card === null || !hasOwn(card, "skills")) {
    return []
  }
  const skills = (card as { skills: unknown }).skills
  if (!Array.isArray(skills)) return []
  return skills.flatMap((skill) => {
    if (typeof skill !== "object" || skill === null || !hasOwn(skill, "id")) return []
    const id = (skill as { id: unknown }).id
    return typeof id === "string" && id.length > 0 ? [id] : []
  })
}

export async function sendGemmaVisualReviewIfAdvertised(options: {
  card: unknown
  sendMessage: (message: SendMessageParams) => Promise<PostA2AResult>
  messageId: string
  params: Record<string, unknown>
}): Promise<PostA2AResult | null> {
  if (!skillIdsFromAgentCard(options.card).includes(GEMMA_VISUAL_REVIEW_SKILL_ID)) {
    return null
  }
  return options.sendMessage({
    messageId: options.messageId,
    text: GEMMA_VISUAL_REVIEW_SKILL_ID,
    skillId: GEMMA_VISUAL_REVIEW_SKILL_ID,
    params: options.params,
  })
}

export async function fetchAgentCard(baseUrl: string): Promise<AgentCardResult> {
  const response = await fetch(`${baseUrl}/.well-known/agent-card.json`, {
    method: 'GET',
  })
  let card: unknown
  try {
    card = await response.json()
  } catch {
    card = null
  }
  return {
    ok: response.ok,
    status: response.status,
    card,
  }
}

/**
 * Live 실측 application-id — Studio does not invent another id.
 *
 * `cogo-bot` is the canonical appliance bot: measured live 2026-08-24 it
 * advertises kg-sparql, gemma-visual-review, cogo-surface-preview AND
 * agent-answer, while the older `demo` publication still advertises only the
 * first two. With the fallback pointed at `demo`, every Studio project whose
 * own slug has no publication silently reached the two-skill generation --
 * no surface preview, no on-box model answer.
 */
export const APPLIANCE_LIVE_AGENT_APPLICATION_ID = "cogo-bot"

/**
 * The bot this workspace talks to, from the project slug when there is one.
 *
 * ONE derivation for every consumer. The roster strip and the send path used
 * to decide separately, and they disagreed: with no project the strip fell
 * back to the resident bot and advertised it while the send path returned
 * early, so the UI named a bot the message never reached (measured live
 * 2026-08-24). A shared function is what keeps them from drifting again.
 *
 * Cogo Bot is the resident participant of every thread, so "no project" is a
 * legitimate state that still has a bot -- not a reason to stay silent.
 */
export function studioBotApplicationId(slug: string | null | undefined): string {
  const candidate = (slug ?? "").trim()
  return APPLICATION_ID.test(candidate) ? candidate : APPLIANCE_LIVE_AGENT_APPLICATION_ID
}

export async function resolveAdvertisedAgentRuntime(
  origin: string,
  preferredId: string,
): Promise<{ applicationId: string; status: number; card: unknown } | null> {
  const ids: string[] = []
  const preferred = preferredId.trim()
  if (APPLICATION_ID.test(preferred)) ids.push(preferred)
  if (!ids.includes(APPLIANCE_LIVE_AGENT_APPLICATION_ID)) {
    ids.push(APPLIANCE_LIVE_AGENT_APPLICATION_ID)
  }
  let last: { applicationId: string; status: number; card: unknown } | null = null
  for (const id of ids) {
    let result: AgentCardResult
    try {
      result = await fetchAgentCard(applianceAgentRuntimeBaseUrl(origin, id))
    } catch {
      result = { ok: false, status: 0, card: null }
    }
    last = { applicationId: id, status: result.status, card: result.card }
    if (result.ok) return last
  }
  return last
}

function a2aAuthHeaders(auth: PostA2AAuth): Record<string, string> {
  if ('accessToken' in auth) {
    return {
      'content-type': 'application/json',
      [A2A_VERSION_HEADER]: A2A_PROTOCOL_VERSION,
      Authorization: `Bearer ${auth.accessToken}`,
    }
  }
  const headerName = sharedKeyHeaderName(auth.headerName)
  return {
    'content-type': 'application/json',
    [A2A_VERSION_HEADER]: A2A_PROTOCOL_VERSION,
    [headerName]: auth.key,
  }
}

export async function postA2A(
  baseUrl: string,
  options: PostA2AOptions,
): Promise<PostA2AResult> {
  const { body: requestBody, ...auth } = options
  const headers = a2aAuthHeaders(auth)
  const response = await fetch(`${baseUrl}/a2a`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  })
  // Read the body defensively. `response.json()` REJECTS on a body that is not
  // JSON, and an edge that fails before reaching the runtime answers with its
  // own HTML -- nginx's 502 page. That rejection threw away the status with it,
  // so the most common real failure arrived at the caller as an exception
  // carrying no evidence, and every screen reported it as "could not reach the
  // bot" with nothing to act on. The status is the evidence; keep it.
  let body: unknown
  try {
    body = await response.json()
  } catch {
    body = undefined
  }
  if (isUnauthorizedDenial(response.status, body)) {
    return {
      ok: false,
      unauthorized: true,
      status: response.status,
      body,
    }
  }
  return {
    ok: response.ok,
    unauthorized: false,
    status: response.status,
    body,
  }
}

export async function sendMessage(
  baseUrl: string,
  options: SendMessageOptions,
): Promise<PostA2AResult> {
  const { text, messageId, id, skillId, params, ...auth } = options
  return postA2A(baseUrl, {
    ...auth,
    body: sendMessageRpcBody({ text, messageId, id, skillId, params }),
  })
}

export function countAgentCardSkills(card: unknown): number {
  if (typeof card !== 'object' || card === null || !hasOwn(card, 'skills')) {
    return 0
  }
  const skills = (card as { skills: unknown }).skills
  return Array.isArray(skills) ? skills.length : 0
}

/**
 * The `${code}: ${message}` shape every refusal text part carries.
 *
 * `adapter.ts` writes it in three places -- `failedStatus`, `terminalStatus`
 * and `safeMessage` -- so a reply whose `metadata.code` was dropped in transit
 * still names itself in its own text. Lowercase-with-underscores is the shape
 * the producer's codes actually have; a renamed `emptyRoster` does NOT match,
 * which is deliberate: it is reported verbatim from `metadata.code` instead of
 * being quietly folded into the code it was renamed from.
 */
const CODE_PREFIXED_TEXT = /^([a-z][a-z0-9_]*): /

function partText(part: unknown): string | undefined {
  if (typeof part !== 'object' || part === null) return undefined
  if (hasOwn(part, 'content')) {
    const content = (part as { content: unknown }).content
    if (typeof content === 'object' && content !== null && hasOwn(content, '$case') && hasOwn(content, 'value')) {
      const boxed = content as { $case: unknown; value: unknown }
      if (boxed.$case === 'text' && typeof boxed.value === 'string') return boxed.value
    }
  }
  if (hasOwn(part, 'text') && typeof (part as { text: unknown }).text === 'string') {
    return (part as { text: string }).text
  }
  return undefined
}

/**
 * The code a reply names itself with, whatever it is.
 *
 * This used to hold a hand-written copy of two of the gateway's six
 * `PlanInterpretationCode`s, and a hand-written `value is RosterRefusal`
 * predicate beside it -- which TypeScript never checks against its own body,
 * so widening the list and forgetting the predicate would have compiled in
 * silence. There is no list here any more: the reply's own `metadata.code` is
 * the answer, and deciding which codes Studio has WORDS for is the reading
 * layer's job (`read-agent-reply.ts`), where exactly one table answers it.
 *
 * `deploy-surface.tsx` prints this in its RPC diagnostic line. It used to show
 * nothing for four of the six refusals.
 */
export function rosterRefusalFromRpcBody(body: unknown): string | undefined {
  const refusal = taskFailureFromRpcBody(body) ?? agentMessageReplyFromRpcBody(body)
  if (!refusal || refusal.code === undefined || refusal.code === GENERIC_TASK_FAILURE_CODE) {
    return undefined
  }
  return refusal.code
}

/**
 * A reply the gateway published as an A2A *Message* rather than a task.
 *
 * `refuseContinuation` does exactly this -- `bus.publish(safeMessage(...))` --
 * for `continuation_invalid`, `continuation_replayed`, `continuation_conflict`
 * and `continuation_reconciliation_required`. The A2A SDK's JSON-RPC handler
 * splits on the payload (`"messageId" in messageOrTask ? { message } : { task
 * }`), so those four land under `result.message` -- and every reader in this
 * file walked `result.task`. All four therefore reached the user as "응답을
 * 받았지만 렌더할 표면이 없습니다": the product reporting that nothing arrived
 * while a named, single-word refusal sat in the body.
 *
 * (The SDK package is deliberately NOT named in an import-shaped spelling
 * here: the closure gate below in `__tests__/studio-agent-runtime-a2a.test.ts`
 * scans this file's raw text, comments included.)
 *
 * `code` is optional because a Message reply is not necessarily a refusal.
 * Absent code + text is prose, and prose is an answer.
 */
export function agentMessageReplyFromRpcBody(
  body: unknown,
): { code?: string; detail: string } | undefined {
  if (typeof body !== 'object' || body === null || !hasOwn(body, 'result')) return undefined
  const result = (body as { result: unknown }).result
  if (typeof result !== 'object' || result === null || !hasOwn(result, 'message')) return undefined
  const message = (result as { message: unknown }).message
  if (typeof message !== 'object' || message === null) return undefined
  const detail = firstPartText(message)
  const code = declaredCode(message) ?? codeFromText(detail)
  if (code === undefined && detail.length === 0) return undefined
  return code === undefined ? { detail } : { code, detail }
}

/** Same diagnostic segment the launch surface prints. Not a success label. */
export function formatAgentRuntimeRpcLine(rpc: {
  status: number
  unauthorized: boolean
  rosterRefusal?: string | null
}): string {
  return `RPC HTTP ${rpc.status}${rpc.unauthorized ? " · unauthorized" : ""}${rpc.rosterRefusal ? ` · ${rpc.rosterRefusal}` : ""}`
}

/** Binds same-origin `/apps/<id>` + identity Bearer or launcher header/key. Does not assemble container DNS. */
export function createAgentRuntimeClient(options: AgentRuntimeClientOptions): {
  baseUrl: string
  fetchAgentCard: () => Promise<AgentCardResult>
  postA2A: (body: unknown) => Promise<PostA2AResult>
  sendMessage: (message: SendMessageParams) => Promise<PostA2AResult>
} {
  const { origin, applicationId, ...auth } = options
  const baseUrl = applianceAgentRuntimeBaseUrl(origin, applicationId)
  return {
    baseUrl,
    fetchAgentCard: () => fetchAgentCard(baseUrl),
    postA2A: (body) => postA2A(baseUrl, { ...auth, body }),
    sendMessage: (message) => sendMessage(baseUrl, { ...message, ...auth }),
  }
}
